/**
 * Global AudioContext utility for iOS-compatible audio playback.
 * 
 * iOS requires AudioContext to be created and resumed during a user gesture.
 * This utility provides a singleton AudioContext that can be unlocked on button
 * click in ChatPage before navigating to the call page.
 */

let globalAudioContext = null;
const audioBufferCache = new Map();

const createAudioContext = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    console.log('[AudioContext] Created, state:', ctx.state);
    return ctx;
};

/**
 * Get or create the global AudioContext singleton.
 * @returns {AudioContext} The global AudioContext instance
 */
export const getGlobalAudioContext = () => {
    if (!globalAudioContext || globalAudioContext.state === 'closed') {
        globalAudioContext = createAudioContext();
    }
    return globalAudioContext;
};

const ensureRunningAudioContext = async () => {
    const ctx = getGlobalAudioContext();

    if (ctx.state === 'suspended') {
        await ctx.resume();
    }

    if (ctx.state === 'closed') {
        globalAudioContext = createAudioContext();
        if (globalAudioContext.state === 'suspended') {
            await globalAudioContext.resume();
        }
        return globalAudioContext;
    }

    return ctx;
};

/**
 * Unlock the AudioContext. MUST be called during a user gesture (click/tap).
 * This is critical for iOS audio playback to work.
 * @returns {Promise<boolean>} True if unlocked successfully
 */
export const unlockAudioContext = async () => {
    const ctx = await ensureRunningAudioContext();
    
    if (ctx.state === 'suspended') {
        try {
            await ctx.resume();
            console.log('[AudioContext] Unlocked, state:', ctx.state);
            
            // iOS sometimes needs a silent buffer played to fully unlock
            const silentBuffer = ctx.createBuffer(1, 1, 22050);
            const source = ctx.createBufferSource();
            source.buffer = silentBuffer;
            source.connect(ctx.destination);
            source.start(0);
            
            return true;
        } catch (e) {
            console.error('[AudioContext] Failed to unlock:', e);
            return false;
        }
    }
    
    console.log('[AudioContext] Already running, state:', ctx.state);
    return true;
};

/**
 * Check if the AudioContext is unlocked and ready to play audio.
 * @returns {boolean} True if ready
 */
export const isAudioContextReady = () => {
    return globalAudioContext && globalAudioContext.state === 'running';
};

/**
 * Load and cache an audio file as an AudioBuffer.
 * @param {string} url - The URL of the audio file
 * @returns {Promise<AudioBuffer>} The decoded audio buffer
 */
export const loadAudioBuffer = async (url) => {
    if (audioBufferCache.has(url)) {
        return audioBufferCache.get(url);
    }
    
    const ctx = await ensureRunningAudioContext();
    
    try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        audioBufferCache.set(url, audioBuffer);
        return audioBuffer;
    } catch (e) {
        console.error('[AudioContext] Failed to load audio:', url, e);
        throw e;
    }
};

/**
 * Play audio using the global AudioContext.
 * @param {string} url - The URL of the audio file to play
 * @param {Object} options - Playback options
 * @param {boolean} options.loop - Whether to loop the audio
 * @param {function} options.onEnded - Callback when audio finishes
 * @returns {Object} Control object with stop() method and source reference
 */
export const playAudioWithGlobalContext = async (url, options = {}) => {
    const { loop = false, onEnded = null } = options;
    const ctx = await ensureRunningAudioContext();
    
    try {
        const audioBuffer = await loadAudioBuffer(url);
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.loop = loop;
        source.connect(ctx.destination);
        
        if (onEnded) {
            source.onended = onEnded;
        }
        
        source.start(0);
        console.log('[AudioContext] Playing:', url);
        
        return {
            source,
            stop: () => {
                try {
                    source.stop();
                    source.disconnect();
                } catch (e) {
                    // Already stopped
                }
            }
        };
    } catch (e) {
        console.error('[AudioContext] Playback failed:', url, e);
        throw e;
    }
};

/**
 * Preload audio files for faster playback later.
 * @param {string[]} urls - Array of audio URLs to preload
 */
export const preloadAudio = async (urls) => {
    await Promise.all(urls.map(url => loadAudioBuffer(url).catch(() => null)));
};

/**
 * Get the current state of the global AudioContext.
 * @returns {string|null} The state ('suspended', 'running', 'closed') or null if not created
 */
export const getAudioContextState = () => {
    return globalAudioContext ? globalAudioContext.state : null;
};

export default {
    getGlobalAudioContext,
    unlockAudioContext,
    isAudioContextReady,
    loadAudioBuffer,
    playAudioWithGlobalContext,
    preloadAudio,
    getAudioContextState
};

